/* eslint-disable */

import * as companyManagementService from "../../services/company";
import * as settings from "../../services/settings";

import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { Button, Col, Row, Spin, Table, notification } from "antd";
import { useCallback, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import permissions from "../../../configs/permission";
import { useAccessToken } from "../../contexts/AccessTokenContext";
import { usePermission } from "../../hooks/usePermission";
import { formatNumber } from "../model/formatNumber";
import { ModalDelete } from "../model/modal/delete";
import { ModelCreateFormula } from "./modal/modalCreateFormula";

const initPage = { limit: 10, page: 1, total: 1 };

export const CreateCompanyFormula = ({ t, detailData }) => {
  const { t: tFormular } = useTranslation("company", { keyPrefix: "Company_Create.Create_Formula" });
  const { hasPermission, isCompanyFullAccess } = usePermission();
  const can = (p) => isCompanyFullAccess || hasPermission(p);
  const [search, setSearch] = useState(initPage);
  const { accessToken } = useAccessToken();
  const [companyFormulaData, setCompanyFormulaData] = useState([]);
  const [viewFormulaDataByOne, setViewFormulaData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [prepareDeleteData, setPrepareDeleteData] = useState({});

  const [api, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = (type, message, description) => {
    api[type]({
      message: message,
      description: description,
    });
  };

  const showModal = () => {
    setIsModalOpen(true);
  };
  const handleOk = () => {
    setIsModalOpen(false);
  };
  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const onDeleteCompanyFormula = async (data) => {
    let prepareDeleteCompanyFormula = {
      id: data.id,
    };
    setLoading(true);
    try {
      const response = await companyManagementService.deleteCompanyFormula(accessToken, prepareDeleteCompanyFormula);
      openNotificationWithIcon(settings.NotificationType.SUCCESS, <>{"Success."}</>, <>{"Delete formula success"}</>);
      setPrepareDeleteData({});
      setIsOpenDelete(false);
      fetchCompanyFormula();
      setLoading(false);
    } catch (err) {
      const { message } = settings.getErrorMessage(err);
      openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
      console.error("[CreateCompanyFormula] onDeleteCompanyFormula: ", err);
    }
  };

  const fetchCompanyFormula = useCallback(
    async (param) => {
      setLoading(true);
      const params = { ...param };
      let updateById = detailData.id;
      try {
        const data = await companyManagementService.getCompanyFormula(accessToken, updateById, params);
        setCompanyFormulaData(data);
        setSearch({ ...param });
        setLoading(false);
      } catch (err) {
        console.error("[CreateCompanyFormula] fetchCompanyFormula: ", err);
        const { message } = settings.getErrorMessage(err);
        openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
      }
      setLoading(false);
    },
    [accessToken]
  );

  useEffect(() => {
    setLoading(true);
    fetchCompanyFormula();
    setLoading(false);
  }, [accessToken, fetchCompanyFormula]);

  const renderSumFormula = (dataList, field, route = "sea") => {
    const value = (dataList || []).reduce((sum, item) => {
      return item.route === route ? sum + item[field] : sum;
    }, 0);
    return formatNumber(value);
  };

  const columns = [
    {
      title: tFormular("Table_No"),
      dataIndex: "id",
      width: 70,
    },
    {
      title: tFormular("Table_Origin"),
      dataIndex: "origin",
      width: 100,
      render: (_, record) => {
        const origin = record.app_warehouse_app_companyformula_origin_idToapp_warehouse.warehouse_code || "";
        return <p>{origin}</p>;
      },
    },
    {
      title: tFormular("Table_Destination"),
      dataIndex: "destination",
      width: 130,
      render: (_, record) => {
        const destination = record.app_warehouse_app_companyformula_destination_idToapp_warehouse.warehouse_code || "";
        return <p>{destination}</p>;
      },
    },
    {
      title: tFormular("Threshold") + " A",
      dataIndex: "threshold_a",
      width: 130,
      render: (_, record) => {
        const threshold_a = record.threshold_a || 0;
        return <p>{formatNumber(threshold_a)}</p>;
      },
    },
    {
      title: tFormular("Threshold") + " B",
      dataIndex: "threshold_b",
      width: 130,
      render: (_, record) => {
        const threshold_b = record.threshold_b || "";
        return <p>{formatNumber(threshold_b)}</p>;
      },
    },
    {
      title: tFormular("Threshold") + " C",
      dataIndex: "threshold_c",
      width: 130,
      render: (_, record) => {
        const threshold_c = record.threshold_c || "";
        return <p>{formatNumber(threshold_c)}</p>;
      },
    },
    {
      title: tFormular("Threshold") + " D",
      dataIndex: "threshold_d",
      width: 130,
      render: (_, record) => {
        const threshold_d = record.threshold_d || "";
        return <p>{formatNumber(threshold_d)}</p>;
      },
    },
    {
      title: tFormular("Threshold") + " E",
      dataIndex: "threshold_e",
      width: 130,
      render: (_, record) => {
        const threshold_e = record.threshold_e || "";
        return <p>{formatNumber(threshold_e)}</p>;
      },
    },
    // volume sea
    {
      title: `${tFormular("Volume")} ${tFormular("Sea")} ${tFormular("From")}`,
      width: 150,
      className: "bg-table-light-blue",
      render: (_, record) => renderSumFormula(record.app_companyvolumeformula, "volume_from", "sea"),
    },
    {
      title: `${tFormular("Volume")} ${tFormular("Sea")} ${tFormular("To")}`,
      width: 150,
      className: "bg-table-light-blue",
      render: (_, record) => renderSumFormula(record.app_companyvolumeformula, "volume_to", "sea"),
    },
    {
      title: `${tFormular("Volume")} ${tFormular("Sea")} A`,
      width: 140,
      className: "bg-table-light-blue",
      render: (_, record) => renderSumFormula(record.app_companyvolumeformula, "a", "sea"),
    },
    {
      title: `${tFormular("Volume")} ${tFormular("Sea")} B`,
      width: 140,
      className: "bg-table-light-blue",
      render: (_, record) => renderSumFormula(record.app_companyvolumeformula, "b", "sea"),
    },
    {
      title: `${tFormular("Volume")} ${tFormular("Sea")} C`,
      width: 140,
      className: "bg-table-light-blue",
      render: (_, record) => renderSumFormula(record.app_companyvolumeformula, "c", "sea"),
    },
    {
      title: `${tFormular("Volume")} ${tFormular("Sea")} D`,
      width: 140,
      className: "bg-table-light-blue",
      render: (_, record) => renderSumFormula(record.app_companyvolumeformula, "d", "sea"),
    },
    {
      title: `${tFormular("Volume")} ${tFormular("Sea")} E`,
      width: 140,
      className: "bg-table-light-blue",
      render: (_, record) => renderSumFormula(record.app_companyvolumeformula, "e", "sea"),
    },
    // volume land
    {
      title: `${tFormular("Volume")} ${tFormular("Land")} ${tFormular("From")}`,
      width: 150,
      className: "bg-table-light-red",
      render: (_, record) => renderSumFormula(record.app_companyvolumeformula, "volume_from", "land"),
    },
    {
      title: `${tFormular("Volume")} ${tFormular("Land")} ${tFormular("To")}`,
      width: 150,
      className: "bg-table-light-red",
      render: (_, record) => renderSumFormula(record.app_companyvolumeformula, "volume_to", "land"),
    },
    {
      title: `${tFormular("Volume")} ${tFormular("Land")} A`,
      width: 150,
      className: "bg-table-light-red",
      render: (_, record) => renderSumFormula(record.app_companyvolumeformula, "a", "land"),
    },
    {
      title: `${tFormular("Volume")} ${tFormular("Land")} B`,
      width: 150,
      className: "bg-table-light-red",
      render: (_, record) => renderSumFormula(record.app_companyvolumeformula, "b", "land"),
    },
    {
      title: `${tFormular("Volume")} ${tFormular("Land")} C`,
      width: 150,
      className: "bg-table-light-red",
      render: (_, record) => renderSumFormula(record.app_companyvolumeformula, "c", "land"),
    },
    {
      title: `${tFormular("Volume")} ${tFormular("Land")} D`,
      width: 150,
      className: "bg-table-light-red",
      render: (_, record) => renderSumFormula(record.app_companyvolumeformula, "d", "land"),
    },
    {
      title: `${tFormular("Volume")} ${tFormular("Land")} E`,
      width: 150,
      className: "bg-table-light-red",
      render: (_, record) => renderSumFormula(record.app_companyvolumeformula, "e", "land"),
    },
    // weight sea
    {
      title: `${tFormular("Weight")} ${tFormular("Sea")} ${tFormular("From")}`,
      width: 150,
      className: "bg-table-light-blue",
      render: (_, record) => renderSumFormula(record.app_companyweightformula, "weight_from", "sea"),
    },
    {
      title: `${tFormular("Weight")} ${tFormular("Sea")} ${tFormular("To")}`,
      width: 150,
      className: "bg-table-light-blue",
      render: (_, record) => renderSumFormula(record.app_companyweightformula, "weight_to", "sea"),
    },
    {
      title: `${tFormular("Weight")} ${tFormular("Sea")} A`,
      width: 150,
      className: "bg-table-light-blue",
      render: (_, record) => renderSumFormula(record.app_companyweightformula, "a", "sea"),
    },
    {
      title: `${tFormular("Weight")} ${tFormular("Sea")} B`,
      width: 150,
      className: "bg-table-light-blue",
      render: (_, record) => renderSumFormula(record.app_companyweightformula, "b", "sea"),
    },
    {
      title: `${tFormular("Weight")} ${tFormular("Sea")} C`,
      width: 150,
      className: "bg-table-light-blue",
      render: (_, record) => renderSumFormula(record.app_companyweightformula, "c", "sea"),
    },
    {
      title: `${tFormular("Weight")} ${tFormular("Sea")} D`,
      width: 150,
      className: "bg-table-light-blue",
      render: (_, record) => renderSumFormula(record.app_companyweightformula, "d", "sea"),
    },
    {
      title: `${tFormular("Weight")} ${tFormular("Sea")} E`,
      width: 150,
      className: "bg-table-light-blue",
      render: (_, record) => renderSumFormula(record.app_companyweightformula, "e", "sea"),
    },
    // volume land
    {
      title: `${tFormular("Weight")} ${tFormular("Land")} ${tFormular("From")}`,
      width: 150,
      className: "bg-table-light-red",
      render: (_, record) => renderSumFormula(record.app_companyweightformula, "weight_from", "land"),
    },
    {
      title: `${tFormular("Weight")} ${tFormular("Land")} ${tFormular("To")}`,
      width: 150,
      className: "bg-table-light-red",
      render: (_, record) => renderSumFormula(record.app_companyweightformula, "weight_to", "land"),
    },
    {
      title: `${tFormular("Weight")} ${tFormular("Land")} A`,
      width: 150,
      className: "bg-table-light-red",
      render: (_, record) => renderSumFormula(record.app_companyweightformula, "a", "land"),
    },
    {
      title: `${tFormular("Weight")} ${tFormular("Land")} B`,
      width: 150,
      className: "bg-table-light-red",
      render: (_, record) => renderSumFormula(record.app_companyweightformula, "b", "land"),
    },
    {
      title: `${tFormular("Weight")} ${tFormular("Land")} C`,
      width: 150,
      className: "bg-table-light-red",
      render: (_, record) => renderSumFormula(record.app_companyweightformula, "c", "land"),
    },
    {
      title: `${tFormular("Weight")} ${tFormular("Land")} D`,
      width: 150,
      className: "bg-table-light-red",
      render: (_, record) => renderSumFormula(record.app_companyweightformula, "d", "land"),
    },
    {
      title: `${tFormular("Weight")} ${tFormular("Land")} E`,
      width: 150,
      className: "bg-table-light-red",
      render: (_, record) => renderSumFormula(record.app_companyweightformula, "e", "land"),
    },

    {
      title: t("Company_Table.Table_Edit"),
      dataIndex: "edit",
      width: 90,
      render: (_, record) => (
        <Button
          type="link"
          disabled={!can(permissions.CompanyFormula.CHANGE)}
          onClick={() => {
            setViewFormulaData(record);
            showModal();
          }}
        >
          <EditOutlined />
        </Button>
      ),
    },
    {
      title: t("Company_Table.Table_Delete"),
      dataIndex: "delete",
      width: 90,
      render: (_, record) => (
        <Button
          type="link"
          disabled={!can(permissions.CompanyFormula.DELETE)}
          onClick={() => {
            setIsOpenDelete(true);
            setPrepareDeleteData(record);
          }}
        >
          <DeleteOutlined />
        </Button>
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      {contextHolder}
      <Row align="middle" justify="end">
        <Col span={24}>
          <Table
            columns={columns}
            dataSource={companyFormulaData}
            scroll={{ x: "3000px", y: "500px" }}
            rowKey={(record) => record.id}
            pagination={{
              defaultPageSize: search.limit,
              current: search.page,
              pageSize: search.limit,
              total: search.total,
              onChange: (page, limit) => {
                fetchCompany({ ...search, page, limit });
              },
              showSizeChanger: true,
            }}
            style={{ cursor: "pointer" }}
          />
        </Col>
        <Row justify="end" style={{ marginTop: "10px" }}>
          <Col span={24}>
            <Button
              type="primary"
              onClick={() => {
                fetchCompanyFormula();
              }}
              style={{ marginRight: "10px", justifyContent: "end" }}
            >
              {t("BTN_Reload", { ns: "buttons" })}
            </Button>
          </Col>
        </Row>
        <ModelCreateFormula
          isModalOpen={isModalOpen}
          handleOk={handleOk}
          viewFormulaDataByOne={viewFormulaDataByOne}
          handleCancel={handleCancel}
          fetchCompanyFormula={fetchCompanyFormula}
          setIsModalOpen={setIsModalOpen}
          t={t}
        />
      </Row>
      <ModalDelete
        isOpen={isOpenDelete}
        handleCloseModal={() => {
          setIsOpenDelete(false);
        }}
        onClick={() => {
          onDeleteCompanyFormula(prepareDeleteData);
        }}
        dataDetail={prepareDeleteData}
        t={t}
      />
    </Spin>
  );
};
